import { Drawer, Dropdown, MenuProps, Select, Tabs, Progress, Badge } from 'antd'
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { logout } from '../../utils/authSlice';
import { hydrateGamification } from '../../utils/gamificationSlice';
import { broadcastLogout } from '../../utils/crossTabAuthSync';
import { useGemificationsQuery, useGetPatientListQuery, useGetProfileDetailQuery } from '../../utils/apiSlice';
import { Tooltip } from 'react-tooltip';
import {
  getEffectiveUserData,
  readActiveProfile,
  readPrimaryUserData,
  setActiveProfileRelated,
  setActiveProfileSelf,
} from '../../utils/activeProfileStorage';
import "./header.scss";
import CoinSystem from "../../pages/CoinWallet/CoinWallet"
import { CloseOutlined } from '@ant-design/icons';
import CoinPop, { triggerCoins } from "../../pages/canvasConfict";
const { Option } = Select;
const Header = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // const coinBalance = useSelector((s: any) => s.gamification?.balance ?? 0);

  const [coinBalance ,setCoinBalance] =useState(0)

  const userData = readPrimaryUserData();

 const getStoredUser = () => {
    // Try localStorage
    const localData = localStorage.getItem("userData");
    if (localData) return JSON.parse(localData);

    // Fallback to sessionStorage
    const sessionData = sessionStorage.getItem("userData");
    if (sessionData) return JSON.parse(sessionData);

    // Return default if neither exists
    return {};
  }

 const storedUser = getStoredUser();
    const userId =
      storedUser?.userId ||
      null;
    const { data: apiResponse, isLoading } = useGemificationsQuery(userId, {
      skip: !userId, // very important
    });

  const primaryUserId = userData?.userId || userData?.id;
  const selfSelectValue = String(primaryUserId || "self");
  const { data: relatedUsersData } = useGetPatientListQuery(primaryUserId, {
    skip: !primaryUserId,
  });
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const handleChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);


   useEffect(() => {
     
      if (apiResponse?.data) {
     
         setCoinBalance(apiResponse.data.totalCoins)
      }
    }, [apiResponse]);
  
  const userDropdownOptions = useMemo(() => {
    const byValue = new Map<string, { value: string; label: string }>();

    byValue.set(selfSelectValue, {
      value: selfSelectValue,
      label: "Self",
    });

    if (relatedUsersData?.data && Array.isArray(relatedUsersData.data)) {
      relatedUsersData.data.forEach((member: any) => {
        const rid = String(member.relatedUserId || member.memberId || member.relationshipId || "");
        if (!rid || rid === selfSelectValue) return;
        const name = member.fullName ||
          [member.firstName, member.middleName, member.lastName].filter(Boolean).join(" ") ||
          "Family Member";
        const relation = member.relationshipType
          ? ` (${member.relationshipType.charAt(0).toUpperCase() + member.relationshipType.slice(1).toLowerCase()})`
          : "";
        byValue.set(rid, { value: rid, label: `${name}${relation}` });
      });
    }

    // Reload: API list abhi aaya nahi — session me jo member select hai uska option add karo (Select hilna band)
    const ap = readActiveProfile();
    if (ap.mode === "related") {
      const v = String(ap.relatedUserId);
      if (v && !byValue.has(v)) {
        const nm = [ap.firstName, ap.middleName, ap.lastName].filter(Boolean).join(" ").trim();
        byValue.set(v, {
          value: v,
          label: nm ? `${nm} (family)` : "Selected member",
        });
      }
    }

    return Array.from(byValue.values());
  }, [relatedUsersData, selfSelectValue]);

  const [selectedUser, setSelectedUser] = useState<string>(() => {
    const ap = readActiveProfile();
    const selfVal = String(
      (readPrimaryUserData()?.userId || readPrimaryUserData()?.id || "self") as string
    );
    return ap.mode === "related" ? String(ap.relatedUserId) : selfVal;
  });

  const [headerProfile, setHeaderProfile] = useState<Record<string, any>>(() =>
    getEffectiveUserData()
  );

  // Profile switch (dusre tab / same tab)
  useEffect(() => {
    const sync = () => {
      setHeaderProfile(getEffectiveUserData());
      const ap = readActiveProfile();
      setSelectedUser(ap.mode === "related" ? String(ap.relatedUserId) : selfSelectValue);
    };
    window.addEventListener("activeProfileChanged", sync);
    return () => window.removeEventListener("activeProfileChanged", sync);
  }, [selfSelectValue]);

  // Self: login userId load hone par value "self" -> real id (dropdown stable)
  useEffect(() => {
    const ap = readActiveProfile();
    if (ap.mode !== "related") {
      setSelectedUser(selfSelectValue);
    }
  }, [selfSelectValue]);

  useEffect(() => {
    if (primaryUserId) dispatch(hydrateGamification(String(primaryUserId)));
  }, [primaryUserId, dispatch]);

  const formatName = (name: string) => {
    if (!name) return "";
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  /** Same patient detail API as Profile page — name in header matches questionnaire record. */
  const effectivePatientId = headerProfile?.patientId;
  const { data: patientProfile = {} } = useGetProfileDetailQuery(effectivePatientId, {
    skip: !effectivePatientId,
  });

  const displayName = useMemo(() => {
    const fromApi = [
      formatName(patientProfile.firstName || ""),
      formatName(patientProfile.middleName || ""),
      formatName(patientProfile.lastName || ""),
    ]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();

    if (fromApi) return fromApi;

    if (headerProfile?.firstName) {
      return [
        formatName(headerProfile.firstName),
        formatName(headerProfile.middleName || ""),
        formatName(headerProfile.lastName || ""),
      ]
        .filter(Boolean)
        .join(" ");
    }

    return "John Doe";
  }, [
    patientProfile.firstName,
    patientProfile.middleName,
    patientProfile.lastName,
    headerProfile?.firstName,
    headerProfile?.middleName,
    headerProfile?.lastName,
  ]);

  // Determine avatar based on gender (prefer patient API when loaded)
  const userGender = (
    patientProfile?.gender ||
    patientProfile?.sexAssignedAtBirth ||
    headerProfile?.gender ||
    ""
  ).toString().toUpperCase();
  const avatarImage = userGender === "MALE"
    ? "/images/avatar04.png"
    : userGender === "FEMALE"
      ? "/images/avatar3.png"
      : "/images/avatar12.png";

  const handleLogout = (e: any) => {
    e.preventDefault();
    dispatch(logout());
    broadcastLogout();
    navigate("/login");
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    console.log('Username:', username, 'Password:', password, 'Remember Me:', rememberMe);
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: (
        <a className="d-flex align-items-center fs-16 py-2" style={{ color: '#8a909d' }} onClick={() => navigate("/profile")}>
          <i className="ti-user me-3 fs-18"></i> Profile
        </a>
      ),
    },
    {
      key: 'logout',
      label: (
        <label onClick={handleLogout} className="d-flex align-items-center fs-16 py-2 mb-0" style={{ color: '#8a909d', cursor: 'pointer', width: '100%' }}>
          <i className="ti-lock me-3 fs-18"></i> Logout
        </label>
      ),
    },
  ];

  const handleFullScreen = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };


  const showProfileSwitcher =
    userDropdownOptions.length > 1 || readActiveProfile().mode === "related";

  const goToDashboardAfterSwitch = () => {
    if (location.pathname !== "/dashboard") {
      navigate("/dashboard");
    }
  };

  const handleProfileSelect = (val: string) => {
    setSelectedUser(val);
    if (String(val) === String(selfSelectValue)) {
      setActiveProfileSelf();
      toast.success("Switched to your profile");
      goToDashboardAfterSwitch();
      return;
    }
    const list = relatedUsersData?.data;
    if (!Array.isArray(list)) return;
    const member = list.find(
      (m: any) =>
        String(m.relatedUserId || m.memberId || m.relationshipId || "") === String(val)
    );
    if (!member) return;

    setActiveProfileRelated(member);

    const displayName =
      member.fullName ||
      [member.firstName, member.middleName, member.lastName].filter(Boolean).join(" ").trim() ||
      "Family member";

    const hasQuestionnaire =
      member.patientId != null &&
      member.patientId !== "" &&
      String(member.patientId).toLowerCase() !== "null";

    if (!hasQuestionnaire) {
      toast.info(
        `Now viewing ${displayName}. Complete their health questionnaire on the dashboard.`,
        { autoClose: 5000 }
      );
    } else {
      toast.success(`Now viewing ${displayName}'s profile`);
    }
    goToDashboardAfterSwitch();
  };


  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };


  const totalCoins= (data:any) =>{
      setCoinBalance(data.totalCoins)
  }

  return (
    
    <header className="main-header">
    
      <div className="d-flex align-items-center logo-box justify-content-start">
        {/* Logo */}
        <a className="logo" onClick={() => navigate("/dashboard")}>
          {/* logo*/}
          <div className="logo-mini w-50" >
            <span className="light-logo">
              <img src="../images/logo-letter.png" alt="logo" />
            </span>
            <span className="dark-logo">
              <img src="../images/logo-letter.png" alt="logo" />
            </span>
          </div>
          {/* <div className="logo-lg">
            <span className="light-logo">
              <img src="../images/logo-dark-text.png" alt="logo" />
            </span>
            <span className="dark-logo">
              <img src="../images/logo-light-text.png" alt="logo" />
            </span>
          </div> */}
        </a>
      </div>

      {/* Header Navbar */}
      <nav className="navbar navbar-static-top">
        {/* Sidebar toggle button */}
        <div className="app-menu">
          <ul className="header-megamenu nav">
            <li className="btn-group nav-item">
              <a
                href="#"
                className="waves-effect waves-light nav-link push-btn btn-primary-light"
                data-toggle="push-menu"
                role="button"
                onClick={(e) => {
                  e.preventDefault();
                  // Ensure sidebar-mini is present on body for proper collapsing styles
                  if (!document.body.classList.contains('sidebar-mini')) {
                    document.body.classList.add('sidebar-mini');
                  }

                  if (window.innerWidth >= 768) {
                    document.body.classList.toggle('sidebar-collapse');
                  } else {
                    document.body.classList.toggle('sidebar-open');
                  }
                }}
              >
                <i className="icon-Menu">
                  <span className="path1"></span>
                  <span className="path2"></span>
                </i>
              </a>
            </li>
            {/* <li className="btn-group d-lg-inline-flex d-none">
              <div className="app-menu">
                <div className="search-bx mx-5">
                  <form>
                    <div className="input-group">
                      <input type="search" className="form-control" placeholder="Search" />
                      <div className="input-group-append">
                        <button className="btn" type="submit" id="button-addon3">
                          <i className="icon-Search">
                            <span className="path1"></span>
                            <span className="path2"></span>
                          </i>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </li> */}
          </ul>
        </div>

        <div className="navbar-custom-menu r-side">
          <ul className="nav navbar-nav">
            {/* User Account */}
            <li className="user user-menu d-flex align-items-center">
              <div className="d-flex pt-1 align-items-center h-100 header-toolbar-group">
                <button
                  data-tooltip-id="fs-tooltip"
                  data-tooltip-content={'Open Rewards & Coins wallet'}
                  type="button"
                  className="header-coin-pill d-inline-flex align-items-center"
                  // onClick={() => navigate("/coin-wallet")}
                  onClick={showDrawer}

                >
                  <span className="header-coin-pill__icon" aria-hidden>🪙</span>
                  <span className="header-coin-pill__balance">{coinBalance}</span>
                </button>
                <Tooltip id="fs-tooltip" className="my-custom-tooltip" place="bottom" />
                {showProfileSwitcher && (
                  <div className="head-role-wrapper header-profile-switcher d-none d-md-block">
                    <Select
                      value={selectedUser}
                      style={{ minWidth: 150 }}
                      className="custom-select-bg head-role-select"
                      variant="borderless"
                      onClick={(e) => { e.stopPropagation(); }}
                      onChange={(val) => handleProfileSelect(String(val))}
                      popupMatchSelectWidth={false}
                    >
                      {userDropdownOptions?.map((opt) => (
                        <Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Option>
                      ))}
                    </Select>
                  </div>
                )}
                <p className="pt-5 fs-14 mb-0 fw-700 text-primary head-role-title">
                  {displayName}
                </p>

                <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']}>
                  <a data-tooltip-id="fs-tooltip"
                    data-tooltip-content={'Account menu'} href="#" className="waves-effect waves-light w-auto l-h-12 bg-transparent no-shadow d-flex align-items-center " onClick={(e) => e.preventDefault()}>

                    <img src={avatarImage} className="avatar rounded-10 bg-primary-light h-40 w-40 head-user-avatar" alt="" />
                  </a>
                </Dropdown>
                <Tooltip id="fs-tooltip" className="my-custom-tooltip" place="bottom" />
              </div>
            </li>

            {/* Fullscreen Button */}
            <li className="btn-group nav-item d-lg-inline-flex d-none">
              <a data-tooltip-id="fs-tooltip"
                data-tooltip-content={isFullScreen ? "Exit Full Screen" : "Full Screen"} href="#" onClick={handleFullScreen} className="waves-effect waves-light nav-link full-screen header-fullscreen-btn" >
                <i className={isFullScreen ? "fas fa-compress" : "fas fa-expand"}></i>
              </a>
              <Tooltip id="fs-tooltip" className="my-custom-tooltip" place="bottom" />
            </li>
          </ul>
        </div>
      </nav>
      {/* <Drawer
        title={
          <div className="drawer-header-custom">
            <div className="tara-logo">T</div>
            <div>
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>Tara Coins</h4>
              <span style={{ fontSize: '12px', color: '#6c757d', fontWeight: '400' }}>Your health rewards</span>
            </div>
          </div>
        }
        placement="right"
        onClose={onClose}
        open={open}
        width={450}
        className="tara-coins-drawer"
        closeIcon={<i className="bi bi-x-lg"></i>}
      >
        <Tabs defaultActiveKey="1" className="tara-tabs">
          <Tabs.TabPane tab="Overview" key="1">
            <div className="balance-card">
              <div className="card-top">
                <span className="label">Your Tara coins</span>
                <div className="coin-display">
                  <span className="coin-icon">T</span>
                  <span className="amount">1,240</span>
                </div>
              </div>
              <div className="badge-row">
                <span className="tara-badge">Health Keeper • Level 14</span>
                <span className="tara-badge">★ 14-day streak</span>
              </div>
              <div className="card-footer">
                <span>+85 this week</span>
                <span>92% med adherence</span>
              </div>
            </div>

            <div className="level-tracker">
              <div className="lvl-box">
                <small>LV.</small>
                <strong>14</strong>
              </div>
              <div className="lvl-progress-area">
                <div className="lvl-info">
                  <span>360 coins to Level 15</span>
                </div>
                <Progress
                  percent={70}
                  showInfo={false}
                  strokeColor="#f39c12"
                  trailColor="#3d4251"
                  strokeWidth={6}
                />
                <div className="lvl-footer">
                  <span>1,240 / 1,600 • Health Keeper</span>
                </div>
              </div>
            </div>

            <h5 className="section-title">QUICK LOG</h5>
            <div className="quick-log-grid">
              <div className="log-item">
                <span className="emoji">🏃</span>
                <span className="name">Steps</span>
                <span className="points">+5</span>
              </div>
              <div className="log-item">
                <span className="emoji">💧</span>
                <span className="name">Water</span>
                <span className="points">+5</span>
              </div>
              <div className="log-item">
                <span className="emoji">🧘</span>
                <span className="name">Exercise</span>
                <span className="points">+8</span>
              </div>
            </div>

            <div className="section-header">
              <h5 className="section-title">🏆 DAILY MISSIONS</h5>
              <span className="mission-count">0 / 2 done</span>
            </div>

            <div className="mission-card">
              <div className="mission-info">
                <span>🏃 Walk 10,000 steps</span>
                <span className="reward-tag">+25</span>
              </div>
              <Progress percent={82} strokeColor="#1a6d54" showInfo={false} size="small" />
              <small>8,241 / 10,000 via Apple / Google Health</small>
            </div>

            <div className="mission-card">
              <div className="mission-info">
                <span>💧 Drink 8 glasses of water</span>
                <span className="reward-tag">+15</span>
              </div>
              <Progress percent={75} strokeColor="#1a6d54" showInfo={false} size="small" />
              <small>6 / 8 glasses</small>
            </div>

          </Tabs.TabPane>
          <Tabs.TabPane tab="Earn" key="2" disabled />
          <Tabs.TabPane tab="Levels" key="3" disabled />
          <Tabs.TabPane tab="Rewards" key="4" disabled />
        </Tabs>
      </Drawer> */}
      <Drawer
        title={
          <div className="drawer-header-custom">
            <div className="tara-logo">T</div>
            <div>
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>Tara Coins</h4>
              <span style={{ fontSize: '12px', color: '#6c757d', fontWeight: '400' }}>Your health rewards</span>
            </div>
          </div>
        }
        placement="right"
        onClose={onClose}
        open={open}
        width={450}
        className="tara-coins-drawer"
        closeIcon={<CloseOutlined style={{ fontSize: '18px', color: '#666' }} />}
      >
        <CoinSystem totalCoins ={totalCoins}/>
      </Drawer>
    </header>



  );
};

export default Header;
